'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import {
  addIaPurchaseOrderLine,
  cancelIaPurchaseOrder,
  createAndPostIaReceipt,
  deleteIaPurchaseOrderLine,
  fetchIaLastVendorUnitCost,
  fetchIaPurchaseOrder,
  queryIaVendors,
  submitIaPurchaseOrder,
  updateIaPurchaseOrder,
  type IaPurchaseOrder,
  type IaVendor,
} from '@/hooks/useInventoryAuditorPurchasing';
import { useInventoryAuditorLocationsList } from '@/hooks/useInventoryAuditorLocation';
import { useNotification } from '@/contexts/NotificationContext';
import CatalogItemAutocomplete, { type CatalogItemOption } from '../purchasing/CatalogItemAutocomplete';
import { searchInventoryAuditorCatalogItems } from '../purchasing/catalogSearch';
import { formatVendorUnitCostDollars, vendorUnitCostHelperText } from '../purchasing/vendorUnitCostHint';
import type { VendorItemUnitCostHint } from '../purchasing/vendorUnitCostHint';

const InventoryAuditorPurchaseOrderDetailPage: React.FC = () => {
  const { poId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { data: locations = [] } = useInventoryAuditorLocationsList();
  const [vendors, setVendors] = useState<IaVendor[]>([]);
  const [po, setPo] = useState<IaPurchaseOrder | null>(null);
  const [headerModalOpen, setHeaderModalOpen] = useState(false);
  const [receiveQty, setReceiveQty] = useState<Record<number, string>>({});
  const [receiveCost, setReceiveCost] = useState<Record<number, string>>({});
  const [receiptNumber, setReceiptNumber] = useState('');
  const [selectedCatalogItem, setSelectedCatalogItem] = useState<CatalogItemOption | null>(null);
  const [newQty, setNewQty] = useState('1');
  const [newCost, setNewCost] = useState('');
  const [addLineCostHint, setAddLineCostHint] = useState<VendorItemUnitCostHint | null>(null);

  const load = useCallback(async () => {
    const id = parseInt(poId ?? '', 10);
    if (Number.isNaN(id)) return;
    try {
      const data = await fetchIaPurchaseOrder(id);
      setPo(data);
      const q: Record<number, string> = {};
      const c: Record<number, string> = {};
      for (const line of data.lines ?? []) {
        const remaining = Math.max(0, line.orderedQty - line.receivedQty);
        q[line.id] = String(remaining);
        c[line.id] = line.unitCostCents != null ? String(line.unitCostCents / 100) : '';
      }
      setReceiveQty(q);
      setReceiveCost(c);
      setReceiptNumber(`GR-${data.poNumber}-${Date.now()}`);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load PO');
    }
  }, [poId, showError]);

  useEffect(() => {
    void load();
    void queryIaVendors().then(setVendors);
  }, [load]);

  useEffect(() => {
    const vendorId = po?.vendor?.id;
    if (!selectedCatalogItem || !vendorId) {
      setAddLineCostHint(null);
      return;
    }
    let cancelled = false;
    void fetchIaLastVendorUnitCost(vendorId, selectedCatalogItem.id).then((hint) => {
      if (cancelled) return;
      setAddLineCostHint(hint);
      if (hint.unitCostCents != null) {
        setNewCost(formatVendorUnitCostDollars(hint.unitCostCents));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedCatalogItem, po?.vendor?.id]);

  const headerFieldGroups = useMemo(
    () => [
      {
        groupName: 'Header',
        fields: [
          {
            label: 'Vendor',
            name: 'vendorId',
            type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
            required: true,
            columns: 12,
            options: vendors.map((v) => ({ label: v.name, value: v.id })),
          },
          {
            label: 'Receive at location',
            name: 'receiveLocationId',
            type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
            required: true,
            columns: 12,
            options: locations.map((l: { id: number; name?: string }) => ({
              label: l.name ?? `Location ${l.id}`,
              value: l.id,
            })),
          },
          { label: 'PO number', name: 'poNumber', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
          { label: 'Notes', name: 'notes', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
        ],
      },
    ],
    [vendors, locations],
  );

  const handleSubmit = async () => {
    if (!po) return;
    if ((po.lines ?? []).length === 0) {
      showError('Add at least one line before submitting');
      return;
    }
    try {
      await submitIaPurchaseOrder(po.id);
      showSuccess('PO submitted');
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Submit failed');
    }
  };

  const handleCancelPo = async () => {
    if (!po || !window.confirm(`Cancel purchase order "${po.poNumber}"?`)) return;
    try {
      await cancelIaPurchaseOrder(po.id);
      showSuccess('Purchase order cancelled');
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Cancel failed');
    }
  };

  const handleReceive = async () => {
    if (!po) return;
    const lines = (po.lines ?? [])
      .map((line) => {
        const receivedQty = parseInt(receiveQty[line.id] ?? '0', 10);
        const dollars = parseFloat(receiveCost[line.id] ?? '0');
        if (receivedQty <= 0) return null;
        return {
          purchaseOrderLineId: line.id,
          receivedQty,
          unitCostCents: Math.round(dollars * 100),
        };
      })
      .filter(Boolean) as Array<{ purchaseOrderLineId: number; receivedQty: number; unitCostCents: number }>;
    if (lines.length === 0) {
      showError('Enter receive qty on at least one line');
      return;
    }
    try {
      await createAndPostIaReceipt({
        purchaseOrderId: po.id,
        receiptNumber: receiptNumber.trim(),
        lines,
      });
      showSuccess('Receipt posted');
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Receive failed');
    }
  };

  const handleAddLine = async () => {
    if (!po) return;
    if (!selectedCatalogItem) {
      showError('Select a catalog item');
      return;
    }
    try {
      await addIaPurchaseOrderLine({
        purchaseOrderId: po.id,
        inventoryItemId: selectedCatalogItem.id,
        orderedQty: parseInt(newQty, 10) || 1,
        unitCostCents: newCost ? Math.round(parseFloat(newCost) * 100) : undefined,
        lineType: 'sku',
      });
      showSuccess('Line added');
      setSelectedCatalogItem(null);
      setAddLineCostHint(null);
      setNewCost('');
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to add line');
    }
  };

  const handleDeleteLine = async (lineId: number) => {
    if (!window.confirm('Remove this line?')) return;
    try {
      await deleteIaPurchaseOrderLine(lineId);
      showSuccess('Line removed');
      await load();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to remove line');
    }
  };

  if (!po) {
    return <Typography sx={{ p: 2 }}>Loading…</Typography>;
  }

  const isDraft = po.status === 'draft';
  const isCancelled = po.status === 'cancelled';
  const canReceive = !isCancelled && po.status !== 'received' && po.status !== 'draft';

  return (
    <>
      {isDraft && (
        <GenericFormModal
          visible={headerModalOpen}
          onDismiss={() => setHeaderModalOpen(false)}
          onSubmit={async (values: Record<string, unknown>) => {
            try {
              await updateIaPurchaseOrder({
                id: po.id,
                vendorId: Number(values.vendorId),
                receiveLocationId: Number(values.receiveLocationId),
                poNumber: String(values.poNumber ?? '').trim(),
                notes: String(values.notes ?? '').trim() || null,
              });
              showSuccess('Header updated');
              setHeaderModalOpen(false);
              await load();
            } catch (e: unknown) {
              showError(e instanceof Error ? e.message : 'Update failed');
            }
          }}
          initialValues={{
            vendorId: po.vendor?.id ?? '',
            receiveLocationId: po.receiveLocation?.id ?? '',
            poNumber: po.poNumber,
            notes: po.notes ?? '',
          }}
          fieldGroups={headerFieldGroups}
          title="Edit purchase order"
        />
      )}

      <TitleBar
        breadcrumbs={[
          { label: 'Home', href: '/app/dashboard' },
          { label: 'Purchase orders', href: '/app/inventory-auditor/purchasing/purchase-orders' },
          { label: po.poNumber },
        ]}
        title={po.poNumber}
        rightContent={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isDraft && (
              <Button variant="outlined" onClick={() => setHeaderModalOpen(true)} sx={{ textTransform: 'none' }}>
                Edit header
              </Button>
            )}
            {po.status !== 'cancelled' && po.status !== 'received' && (
              <Button variant="outlined" color="warning" onClick={() => void handleCancelPo()} sx={{ textTransform: 'none' }}>
                Cancel PO
              </Button>
            )}
          </Box>
        }
      />

      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {po.vendor?.name} → {po.receiveLocation?.name} ·{' '}
          <Chip label={po.status.replace(/_/g, ' ')} size="small" variant="outlined" />
        </Typography>

        {isDraft && (
          <Button variant="contained" onClick={() => void handleSubmit()} sx={{ mb: 2, textTransform: 'none' }}>
            Submit PO
          </Button>
        )}

        {isDraft && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Add line
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <CatalogItemAutocomplete
                value={selectedCatalogItem}
                onChange={setSelectedCatalogItem}
                searchCatalog={searchInventoryAuditorCatalogItems}
                sx={{ minWidth: 320 }}
              />
              <TextField size="small" label="Qty" value={newQty} onChange={(e) => setNewQty(e.target.value)} sx={{ width: 80 }} />
              <TextField
                size="small"
                label="Unit cost ($)"
                value={newCost}
                onChange={(e) => setNewCost(e.target.value)}
                helperText={vendorUnitCostHelperText(addLineCostHint)}
                sx={{ width: 160 }}
              />
              <Button variant="contained" onClick={() => void handleAddLine()} sx={{ textTransform: 'none' }}>
                Add line
              </Button>
            </Box>
          </Paper>
        )}

        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Ordered</TableCell>
              <TableCell align="right">Received</TableCell>
              {canReceive && <TableCell align="right">Receive now</TableCell>}
              {canReceive && <TableCell align="right">Unit cost ($)</TableCell>}
              {isDraft && <TableCell width={48} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {(po.lines ?? []).map((line) => (
              <TableRow key={line.id}>
                <TableCell>
                  {line.inventoryItem?.upc} {line.inventoryItem?.name ? `— ${line.inventoryItem.name}` : ''}
                </TableCell>
                <TableCell align="right">{line.orderedQty}</TableCell>
                <TableCell align="right">{line.receivedQty}</TableCell>
                {canReceive && (
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={receiveQty[line.id] ?? ''}
                      onChange={(e) => setReceiveQty((prev) => ({ ...prev, [line.id]: e.target.value }))}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                )}
                {canReceive && (
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={receiveCost[line.id] ?? ''}
                      onChange={(e) => setReceiveCost((prev) => ({ ...prev, [line.id]: e.target.value }))}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                )}
                {isDraft && (
                  <TableCell>
                    <IconButton
                      size="small"
                      disabled={line.receivedQty > 0}
                      onClick={() => void handleDeleteLine(line.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {canReceive && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Post goods receipt
            </Typography>
            <TextField
              size="small"
              label="Receipt number"
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value)}
              sx={{ mb: 2, display: 'block', maxWidth: 360 }}
            />
            <Button variant="contained" onClick={() => void handleReceive()} sx={{ textTransform: 'none' }}>
              Post receipt to location inventory
            </Button>
          </Paper>
        )}

        <Button sx={{ mt: 2, textTransform: 'none' }} onClick={() => navigate('/app/inventory-auditor/purchasing/purchase-orders')}>
          ← Back to purchase orders
        </Button>
      </Box>
    </>
  );
};

export default InventoryAuditorPurchaseOrderDetailPage;
